/**
 * @file Tap rapide sur mobile : les boutons marques `data-fast` (HUD, onglets, fermeture) s'activent des que le doigt se POSE
 * (pointerdown) au lieu d'attendre le relachement + le `click` natif, qui arrive souvent 100-300 ms plus tard sur un telephone
 * charge (rendu 3D en cours). Le `click` natif qui suit est ensuite avale pour ne pas declencher l'action deux fois.
 * Souris / clavier : comportement natif inchange (click au relachement, Entree/Espace).
 */

/** Delai pendant lequel le `click` natif consecutif a un tap rapide est ignore. */
const SUPPRESS_MS = 700;

/** Branche le tap rapide sur `root` (delegation, phase de capture). Retourne la fonction de nettoyage. */
export const installFastTap = (root: HTMLElement | Document = document): (() => void) => {
  let suppressUntil = 0;
  let synthetic = false;

  const onDown = (e: Event): void => {
    const pe = e as PointerEvent;
    if (pe.pointerType === 'mouse' || !pe.isPrimary) return;
    const target = pe.target instanceof Element ? pe.target.closest<HTMLElement>('[data-fast]') : null;
    if (!target || (target as HTMLButtonElement).disabled || target.getAttribute('aria-disabled') === 'true') return;
    suppressUntil = performance.now() + SUPPRESS_MS;
    synthetic = true;
    try {
      target.click();
    } finally {
      synthetic = false;
    }
  };

  const onClick = (e: Event): void => {
    if (synthetic) return;
    if (performance.now() < suppressUntil) {
      suppressUntil = 0;
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };

  // iOS Safari : sans ecouteur touchstart, l'etat :active (retour visuel d'appui) ne s'affiche pas.
  const noop = (): void => undefined;

  root.addEventListener('pointerdown', onDown, true);
  root.addEventListener('click', onClick, true);
  root.addEventListener('touchstart', noop, { passive: true });
  return () => {
    root.removeEventListener('pointerdown', onDown, true);
    root.removeEventListener('click', onClick, true);
    root.removeEventListener('touchstart', noop);
  };
};
